const SAFE_GROUP = /^\d{1,10}$/;
const SAFE_COMMITTEE = /^[A-Za-z0-9_-]{1,40}$/;
const MAX_DIFFERENCES = 100;

async function sha256(value) {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function canonicalize(value) {
  if (Array.isArray(value)) return `[${value.map(canonicalize).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${canonicalize(value[key])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

function valueType(value) {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
}

function preview(value) {
  if (value === undefined) return "<mancante>";
  if (Array.isArray(value)) return `[Array: ${value.length} elementi]`;
  if (value && typeof value === "object") return `{Oggetto: ${Object.keys(value).length} proprietà}`;
  const serialized = JSON.stringify(value);
  return serialized && serialized.length > 240 ? `${serialized.slice(0, 237)}…` : serialized;
}

function collectDifferences(realValue, r2Value, path = "$", output = []) {
  if (output.length >= MAX_DIFFERENCES || Object.is(realValue, r2Value)) return output;
  const realType = valueType(realValue);
  const r2Type = valueType(r2Value);
  if (realType !== r2Type) {
    output.push({ path, type: "tipo", real: preview(realValue), r2: preview(r2Value) });
    return output;
  }
  if (realType === "array") {
    if (realValue.length !== r2Value.length) {
      output.push({ path, type: "lunghezza", real: String(realValue.length), r2: String(r2Value.length) });
    }
    const length = Math.max(realValue.length, r2Value.length);
    for (let index = 0; index < length && output.length < MAX_DIFFERENCES; index += 1) {
      collectDifferences(realValue[index], r2Value[index], `${path}[${index}]`, output);
    }
    return output;
  }
  if (realType === "object") {
    const keys = [...new Set([...Object.keys(realValue), ...Object.keys(r2Value)])].sort();
    for (const key of keys) {
      if (output.length >= MAX_DIFFERENCES) break;
      collectDifferences(realValue[key], r2Value[key], `${path}.${key}`, output);
    }
    return output;
  }
  output.push({ path, type: "valore", real: preview(realValue), r2: preview(r2Value) });
  return output;
}

async function fetchSource(url, signal) {
  const startedAt = Date.now();
  const requestUrl = new URL(url);
  requestUrl.searchParams.set("r2_compare", `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const response = await fetch(requestUrl, {
    headers: { Accept: "application/json" },
    credentials: "omit",
    signal,
  });
  const text = await response.text();
  let json = null;
  try {
    json = JSON.parse(text);
  } catch (_) {
    // La risposta resta confrontabile come errore HTTP/JSON.
  }
  return {
    url,
    status: response.status,
    ok: response.ok && json !== null,
    text,
    json,
    bytes: new TextEncoder().encode(text).length,
    rawSha256: await sha256(text),
    etag: response.headers.get("etag") || "",
    lastModified: response.headers.get("last-modified") || "",
    cacheStatus: response.headers.get("cf-cache-status") || "",
    durationMs: Date.now() - startedAt,
  };
}

function summarizeSource(result) {
  return {
    url: result.url,
    status: result.status,
    bytes: result.bytes,
    rawSha256: result.rawSha256,
    etag: result.etag,
    lastModified: result.lastModified,
    cacheStatus: result.cacheStatus,
    durationMs: result.durationMs,
  };
}

async function comparePair(name, real, r2) {
  if (!real.ok || !r2.ok) {
    return {
      name,
      equal: false,
      comparable: false,
      real: summarizeSource(real),
      r2: summarizeSource(r2),
      differences: [{ path: "$", type: "http", real: `HTTP ${real.status}`, r2: `HTTP ${r2.status}` }],
      truncated: false,
    };
  }
  const realCanonical = canonicalize(real.json);
  const r2Canonical = canonicalize(r2.json);
  const [realCanonicalSha256, r2CanonicalSha256] = await Promise.all([
    sha256(realCanonical),
    sha256(r2Canonical),
  ]);
  const differences = realCanonicalSha256 === r2CanonicalSha256 ? [] : collectDifferences(real.json, r2.json);
  return {
    name,
    equal: realCanonicalSha256 === r2CanonicalSha256,
    comparable: true,
    real: { ...summarizeSource(real), canonicalSha256: realCanonicalSha256 },
    r2: { ...summarizeSource(r2), canonicalSha256: r2CanonicalSha256 },
    differences,
    truncated: differences.length >= MAX_DIFFERENCES,
  };
}

function extractCommittee(realTables, r2Tables) {
  for (const payload of [realTables, r2Tables]) {
    const root = Array.isArray(payload) ? payload[0] : payload;
    const committee = String(root?.data?.["commettee-id"] || "");
    if (SAFE_COMMITTEE.test(committee)) return committee;
  }
  return "";
}

export async function compareR2Group(groupValue, apiOrigin, r2Origin, signal) {
  const group = String(groupValue || "");
  if (!SAFE_GROUP.test(group)) throw new Error("Girone non valido");
  const [realTables, r2Tables] = await Promise.all([
    fetchSource(`${apiOrigin}/api/v2/tables/${group}`, signal),
    fetchSource(`${r2Origin}/api/v2/tables/${group}`, signal),
  ]);
  const committee = extractCommittee(realTables.json, r2Tables.json);
  if (!committee) throw new Error("Impossibile identificare il comitato del girone");
  const [realCalendar, r2Calendar] = await Promise.all([
    fetchSource(`${apiOrigin}/api/v2/calendar/0/${group}`, signal),
    fetchSource(`${r2Origin}/api/v2/calendar/0/${group}`, signal),
  ]);
  const [calendar, tables] = await Promise.all([
    comparePair("Calendario", realCalendar, r2Calendar),
    comparePair("Classifica", realTables, r2Tables),
  ]);
  return {
    group,
    committee,
    checkedAt: new Date().toISOString(),
    equal: calendar.equal && tables.equal,
    comparisons: { calendar, tables },
  };
}
